import { Alert, Box, Tab, Tabs, Typography } from '@mui/material'
import React, { useMemo, useState, useEffect } from 'react'
import { a11yProps, CustomTabPanel } from './CustomTabPanel'
import CustomerDetailsTab from './CustomerDetailsTab'
import OrderDetailsTab from './OrderDetailsTab'
import ShippingDetailsTab from './ShippingDetailsTab'
import StatusHistoryTab from './StatusHistoryTab'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ROLES } from 'src/constants'

export default function EditOrderSection({
    order,
    tempOrder,
    handleOrderChange,
    validationErrors,
    viewMode = false,
    isSatchelShipment,
    disableEditing = false
}) {
    const { t } = useTranslation()
    const { profile } = useSelector((state) => state.entities.profile)

    const [tab, setTab] = useState(0)
    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };
    const hideCustomer = isSatchelShipment && profile?.merchant?.role === ROLES.merchant;

    const panelsCount = hideCustomer ? 3 : 4;

    useEffect(() => {
        if (tab > panelsCount - 1) setTab(Math.max(0, panelsCount - 1));
    }, [panelsCount, tab]);
    // Helper function to check if a tab has errors
    const tabHasErrors = useMemo(() => {
        if (!validationErrors) return { customer: false, order: false };

        // Customer Details tab errors: user field
        const customerErrors = !!validationErrors.user;

        // Order Details tab errors: warehouse, boxes, orderItems, amountPayable, weight, packagesCount
        const orderErrors = !!(
            validationErrors.warehouse ||
            validationErrors.boxes ||
            validationErrors.orderItems ||
            validationErrors.amountPayable ||
            validationErrors.weight ||
            validationErrors.packagesCount
        );

        return { customer: customerErrors, order: orderErrors };
    }, [validationErrors]);

    // Collect all error messages
    const allErrorMessages = useMemo(() => {
        if (!validationErrors) return [];

        const messages = [];

        const flattenErrors = (obj, prefix = '') => {
            Object.keys(obj).forEach(key => {
                const value = obj[key];
                const fullKey = prefix ? `${prefix}.${key}` : key;

                if (typeof value === 'string') {
                    messages.push(value);
                } else if (typeof value === 'object' && value !== null) {
                    if (Array.isArray(value)) {
                        value.forEach((item, index) => {
                            if (typeof item === 'string') {
                                messages.push(item);
                            } else if (typeof item === 'object' && item !== null) {
                                flattenErrors(item, `${fullKey}[${index}]`);
                            }
                        });
                    } else {
                        flattenErrors(value, fullKey);
                    }
                }
            });
        };

        flattenErrors(validationErrors);
        return messages;
    }, [validationErrors]);

    return (
        <Box sx={{ width: '100%' }}>
            {/* Display all errors */}
            {allErrorMessages.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t("Please fix the following errors")}:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {allErrorMessages.map((message, index) => (
                            <li key={index}>
                                <Typography variant="body2">{message}</Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={handleTabChange} aria-label="order tabs" centered>
                    {
                        (() => {
                            const tabs = [];
                            if (!hideCustomer) {
                                tabs.push({ key: 'customer', label: t('Customer Details'), error: tabHasErrors.customer });
                            }
                            tabs.push({ key: 'order', label: t('Order Details'), error: tabHasErrors.order });
                            tabs.push({ key: 'shipping', label: t('Shipping Details'), error: false });
                            tabs.push({ key: 'status', label: t('Status History'), error: false });

                            return tabs.map((td, idx) => (
                                <Tab
                                    key={td.key}
                                    sx={{
                                        textTransform: 'none',
                                        color: td.error ? 'error.main' : undefined,
                                        '&.Mui-selected': {
                                            color: td.error ? 'error.main' : undefined,
                                        }
                                    }}
                                    label={td.label}
                                    {...a11yProps(idx)}
                                />
                            ));
                        })()
                    }
                </Tabs>
            </Box>

            {
                (() => {
                    const panels = [];
                    if (!hideCustomer) {
                        panels.push({
                            key: 'customer', node: (
                                <CustomerDetailsTab
                                    tempOrder={tempOrder}
                                    handleOrderChange={handleOrderChange}
                                    validationErrors={validationErrors}
                                    viewMode={viewMode}
                                    disableEditing={disableEditing}
                                />)
                        });
                    }

                    panels.push({
                        key: 'order', node: (
                            <OrderDetailsTab
                                tempOrder={tempOrder}
                                handleOrderChange={handleOrderChange}
                                validationErrors={validationErrors}
                                order={order}
                                viewMode={viewMode}
                                isSatchelShipment={isSatchelShipment}
                            />)
                    });

                    panels.push({ key: 'shipping', node: (<ShippingDetailsTab order={order} />) });
                    panels.push({ key: 'status', node: (<StatusHistoryTab order={order} />) });

                    return panels.map((p, idx) => (
                        <CustomTabPanel value={tab} index={idx} key={p.key}>
                            {p.node}
                        </CustomTabPanel>
                    ));
                })()
            }
        </Box>
    )
}
